import { looksPercentEncoded, originOf, type AuthProvider } from '@visious/core';

/**
 * R9's web adapter: the token lives in `localStorage`, keyed **per instance origin**.
 *
 * The key is not decoration. Instances hold different account databases and sign their tokens with
 * different keys, so a token from one is `Invalid signature` at another (§8). Keying by origin
 * means switching instances cannot silently present the wrong credential, and switching *back*
 * does not require signing in again.
 */
const KEY_PREFIX = 'visious.token.';

export interface StoredToken {
  /** The credential. Raw JSON, exactly as it goes into `Authorization: Bearer` — never base64. */
  readonly token: string;
  readonly username: string | null;
  readonly grantedAtEpochSec: number;
}

function keyFor(instanceUrl: string): string | null {
  const origin = originOf(instanceUrl);
  return origin === null ? null : `${KEY_PREFIX}${origin}`;
}

export interface TokenStore {
  read(): StoredToken | null;
  write(value: StoredToken): void;
  clear(): void;
  /** R9 — a token must not survive a move to another instance. */
  clearOtherInstances(): void;
}

/** Storage can throw outright in a partitioned context; that is a signed-out session, not a crash. */
export function createTokenStore(instanceUrl: string): TokenStore {
  const key = keyFor(instanceUrl);

  return {
    read(): StoredToken | null {
      if (key === null) return null;
      let raw: string | null;
      try {
        raw = globalThis.localStorage.getItem(key);
      } catch {
        return null;
      }
      if (raw === null) return null;

      let parsed: unknown;
      try {
        parsed = JSON.parse(raw) as unknown;
      } catch {
        return null;
      }
      if (typeof parsed !== 'object' || parsed === null) return null;
      const record = parsed as Record<string, unknown>;
      const token = record['token'];
      if (typeof token !== 'string' || token === '') return null;
      // The double-encoding trap (§8), caught at the boundary rather than at the call site: a
      // token stored one decode short 403s on every request with no hint as to why.
      if (looksPercentEncoded(token)) return null;

      const username = record['username'];
      const grantedAt = record['grantedAtEpochSec'];
      return {
        token,
        username: typeof username === 'string' && username !== '' ? username : null,
        grantedAtEpochSec: typeof grantedAt === 'number' ? grantedAt : 0,
      };
    },

    write(value: StoredToken): void {
      if (key === null) return;
      try {
        globalThis.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Quota or a blocked context. The session still works; it just will not survive a reload.
      }
    },

    clear(): void {
      if (key === null) return;
      try {
        globalThis.localStorage.removeItem(key);
      } catch {
        /* nothing to do */
      }
    },

    clearOtherInstances(): void {
      try {
        const storage = globalThis.localStorage;
        const doomed: string[] = [];
        for (let i = 0; i < storage.length; i += 1) {
          const candidate = storage.key(i);
          if (candidate !== null && candidate.startsWith(KEY_PREFIX) && candidate !== key) {
            doomed.push(candidate);
          }
        }
        for (const name of doomed) storage.removeItem(name);
      } catch {
        /* nothing to do */
      }
    },
  };
}

/**
 * R9 — core is handed a function, never the token. `headers()` re-reads on every call so a
 * sign-out takes effect on the next request rather than on the next page load.
 */
export function createAuthProvider(read: () => StoredToken | null): AuthProvider {
  return {
    headers(): Readonly<Record<string, string>> {
      const stored = read();
      if (stored === null) return {};
      return { Authorization: `Bearer ${stored.token}` };
    },
  };
}
